import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ReportDesignerDocumentComponent } from '../components/report-designer-document/report-designer-document.component';
import { EventBusService } from '../../../services/event-bus.service';
import { ReportDesignerWidgetConfig } from '../models/report-designer-widget-config';
import { ReportDesignerPage } from '../models/report-designer-page';

/**
 * Stores the state of the Report Designer
 */
@Injectable({
  providedIn: 'root'
})
export class ReportDesignerStorageService {
  /**
   * Widget info mode
   */
  private appWidgetInfoMode = true;

  /**
   * The current template
   */
  private template: any = null;

  /**
   * The current report (behavior subject)
   */
  private _report$: BehaviorSubject<number> = new BehaviorSubject<number>(null);

  /**
   * The current report (as observable)
   */
  public report$: Observable<number> = this._report$.asObservable();

  /**
   * The currency description
   */
  private currencyDescription: string = null;

  /**
   * The unit of length description
   */
  private unitOfLengthDescription: string = null;

  /**
   * The display values over time setting
   */
  private displayValuesOverTimeId: number = null;

  /**
   * Print mode state
   */
  private appPrintMode = false;

  /**
   * The active RD document
   */
  private appActiveDocument: ReportDesignerDocumentComponent = null;

  /**
   * The edit mode (bevavior subject)
   */
  private _appEditMode$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /**
   * The edit mode as observable
   */
  public appEditMode$: Observable<boolean> = this._appEditMode$.asObservable();

  /**
   * The widget header mode (behavior subject)
   */
  private _appWidgetHeaderMode$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /**
   * The widget header mode as observabe
   */
  public appWidgetHeaderMode$: Observable<boolean> = this._appWidgetHeaderMode$.asObservable();

  /**
   * The grid raster mode (behavior subject)
   */
  private _appReportGridRasterMode$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  /**
   * The grid raster mode as observable
   */
  public appReportGridRasterMode$: Observable<boolean> = this._appReportGridRasterMode$.asObservable();

  /**
   * Widget drop mode?
   */
  private widgetTargetDropMode = false;

  /**
   * The widget drop mode widget config
   */
  private widgetTargetDropModeOptions: ReportDesignerWidgetConfig = null;

  /**
   * Constructor
   * @param eventBus EventBusService
   */
  constructor(private eventBus: EventBusService) { }

  /**
   * Gets the current Report Designer document object.
   * @returns The active RD document
   */
  public getActiveDocument(): ReportDesignerDocumentComponent {
    return this.appActiveDocument;
  }

  /**
   * Sets the current Report Designer document object.
   * @param document The RD document
   */
  public setActiveDocument(document: ReportDesignerDocumentComponent): void {
    this.appActiveDocument = document;
    this.eventBus.broadcastEvent("activeDocumentChanged");
  }

  /**
   * Sets the current Report Designer template object.
   * @param newTemplate The template object
   */
  public setTemplate(newTemplate: any): void {
    this.template = newTemplate;
  }

  /**
   * Gets the current Report Designer template object.
   * @returns The template object
   */
  public getTemplate(): any {
    return this.template;
  }

  /**
   * Sets the current report oid
   * @param newReport The current report oid
   */
  public setReport(newReport: number): void {
    this._report$.next(newReport);
  }

  /**
   * Gets the current report oid
   * @returns The current report oid
   */
  public getReport(): number {
    return this._report$.value;
  }

  /**
   * Turns the edit mode on or off. Only used in the Report Designer at the moment because the other areas have dedicated edit dialogs.
   * @param mode New edit mode
   */
  public setAppEditMode(mode: boolean): void {
    setTimeout(() => {
      this._appEditMode$.next(mode);
      this.eventBus.broadcastEvent("editModeToggled");
    });
  }

  /**
   * Gets the current edit mode state. true indicates that the edit mode is turned on, false means that it is turned off.
   * @returns The current edit mode
   */
  public getAppEditMode(): boolean {
    return this._appEditMode$.value;
  }

  /**
   * Sets the current Report Designer Widget Header mode state. true indicates that the editReport Designer Widget Header mode is turned on, false means that it is turned off.
   * If it is turned on, a red utility bar is rendered above every widget.
   * @param mode The new Report Designer Widget Header mode state. true turns the Report Designer Widget Header mode on, false turns it off.
   */
  public setAppWidgetHeaderMode(mode: boolean): void {
    setTimeout(() => {
      const modeChanges = this._appWidgetHeaderMode$.value !== mode;
      if (modeChanges) {
        if (this.appActiveDocument?.data?.pages) {
          for (const page of this.appActiveDocument.data.pages) {
            ReportDesignerPage.applyWidgetHeaderMode(page, mode);
          }
        }

        this.eventBus.broadcastEvent('setAppWidgetHeaderMode', mode);

        this._appWidgetHeaderMode$.next(mode);
      }
    });
  }

  /**
   * Gets the current Report Designer Widget Header mode state. true indicates that the editReport Designer Widget Header mode is turned on, false means that it is turned off.
   * If it is turned on, a red utility bar is rendered above every widget.
   * @returns The current header mode
   */
  public getAppWidgetHeaderMode(): boolean {
    return this._appWidgetHeaderMode$.value;
  }

  /**
   * Sets the current Report Designer Grid Raster state. true indicates that the raster mode is turned on, false means that it is turned off.
   * @param mode The new raster mode
   */
  public setAppReportGridRasterMode(mode: boolean): void {
    this._appReportGridRasterMode$.next(mode);

    this.eventBus.broadcastEvent('setAppReportGridRasterMode', mode);
  }

  /**
   * Gets the current Report Designer Grid Raster state. true indicates that the raster mode is turned on, false means that it is turned off.
   * If it is turned on, the grid in which the widgets can be moved is shown.
   * @returns The current raster mode
   */
  public getAppReportGridRasterMode(): boolean {
    return this._appReportGridRasterMode$.value;
  }

  /**
   * Sets the current Report Designer Widget Info mode state. true indicates that the Report Designer Widget Info mode is turned on, false means that it is turned off.
   * If it is turned on, the Report Designer section's infobar will show more info and configuration possibilities for the currently active widget.
   * @param mode The new info mode
   */
  public setAppWidgetInfoMode(mode: boolean): void {
    this.appWidgetInfoMode = mode;
  }

  /**
   * Gets the current Report Designer Widget Info mode state. true indicates that the Report Designer Widget Info mode is turned on, false means that it is turned off.
   * If it is turned on, the Report Designer section's infobar will show more info and configuration possibilities for the currently active widget.
   * @returns The current info mode
   */
  public getAppWidgetInfoMode(): boolean {
    return this.appWidgetInfoMode;
  }

  /**
   * Sets whether the application is run in print or normal mode
   * @param mode The new print mode
   */
  public setAppPrintMode(mode: boolean): void {
    this.appPrintMode = mode;
  }

  /**
   * Checks whether the application is run in print or normal mode
   * @returns
   */
  public getAppPrintMode(): boolean {
    return this.appPrintMode;
  }

  /**
   * Sets the selected currency description.
   * @param currency The selected currency description
   */
  public setCurrencyDescription(currency: string): void {
    this.currencyDescription = currency;
  }

  /**
   * Gets the currency description on project level
   * @returns T currency description on project level
   */
  public getCurrencyDescription(): string {
    return this.currencyDescription;
  }

  /**
   * Sets the selected unit of length description
   * @param unitOflength The selected unit of length description
   */
  public setUnitOfLength(unitOflength: string): void {
    this.unitOfLengthDescription = unitOflength;
  }

  /**
   * Gets the unit of length  description on project level
   * @returns The unit of length  description on project level
   */
  public getUnitofLength(): string {
    return this.unitOfLengthDescription;
  }

  /**
   * Sets the display values over time mode
   * @param displayValuesOverTimeId The display values over time id
   */
  public setDisplayValuesOverTimeId(displayValuesOverTimeId: number): void {
    this.displayValuesOverTimeId = displayValuesOverTimeId;
  }

  /**
   * Gets the display values over time id on report level
   * @returns The display values over time id
   */
  public getDisplayValuesOverTimeId(): number {
    return this.displayValuesOverTimeId;
  }

  /**
   * Sets the Report Designer widget target drop mode (can the user drop a widget into the page?)
   * @param isEnabled Enabled?
   * @param options Options
   */
  public setWidgetTargetDropMode(isEnabled: boolean, options: ReportDesignerWidgetConfig) {
    this.widgetTargetDropMode = isEnabled;
    this.widgetTargetDropModeOptions = isEnabled ? options : null;
  }

  /**
   * Gets the Report Designer widget target drop mode (can the user drop a widget into the page?)
   * @returns The Report Designer widget target drop mode (can the user drop a widget into the page?)
   */
  public getWidgetTargetDropMode(): boolean {
    return this.widgetTargetDropMode;
  }

  /**
   * Gets the Report Designer widget target drop mode options
   * @returns The Report Designer widget target drop mode options
   */
  public getWidgetTargetDropModeOptions(): ReportDesignerWidgetConfig {
    return this.widgetTargetDropModeOptions;
  }
}
